import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { DatasetModel } from './dataset.model';

const detectChangesParamsSchema = z.object({
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof detectChangesParamsSchema>;
type Errors = { t: string; message?: string }[];

type DetectChangesActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'detectWarehouse'>;
  };
};

/**
 * Runs change detection now, out of band from the dataset's cadence.
 *
 * A warehouse dataset's table can change upstream with no event, so it is
 * normally scanned on a schedule; this is the "check now" that kicks the same
 * debounced evaluation a cadence tick would, without waiting for the next one.
 * Harmless on any kind — it re-evaluates the dataset against its watchers — but
 * it is warehouse datasets that actually have out-of-band changes to find.
 */
export class DetectChangesAction extends BaseAction<Params, Errors> {
  title = 'Detect changes';
  description = 'Re-scan this dataset for changes now';
  inputSchema = detectChangesParamsSchema;
  group = 'Dataset';
  icon = 'arrow-path';

  constructor(private readonly client: DetectChangesActionClient) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    const result = await this.client.resource.dataset.detectWarehouse({
      id: datasetId,
    });
    if (result.isErr()) {
      const message = result.error.err.message ?? 'Failed to detect changes.';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }
    toast.success('Checking for changes…');
    return ok({});
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}
